import React, { useState, useMemo, useCallback } from 'react';
import ReactPlayer from 'react-player';
import { ContentDeliveryInfoResponse, SupportDocumentResponse, SupportDocumentResponseContentTypeEnum } from '@dopingtech/live_expert_bff_api_ts';
import { useApi } from '@/api/useApi';
import { useQueryProvider, useDebounce } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys';
import { LoadingSpinner, Modal, Pagination } from '@/components';
import '@/assets/styles/views/support-documents/support-documents.styles.scss';

type SupportDocTab = 'my-docs' | 'guides';

const VIDEO_TYPES = new Set<string>([
	SupportDocumentResponseContentTypeEnum.Video,
	SupportDocumentResponseContentTypeEnum.ShortFormVideo
]);

const PAGE_SIZE = 9;

function isVideoType(contentType?: string): boolean {
	return VIDEO_TYPES.has(contentType ?? '');
}

function getCardThumbClass(contentType?: string): string {
	return isVideoType(contentType) ? 'support-documents__card-thumb--video' : 'support-documents__card-thumb--document';
}

function getCardIcon(contentType?: string): string {
	if (isVideoType(contentType)) return 'icons-videocamera-record';
	return 'icons-document';
}

function getBadgeClass(contentType?: string): string {
	return isVideoType(contentType) ? 'support-documents__card-type-badge--video' : 'support-documents__card-type-badge--document';
}

function getBadgeLabel(contentType?: string): string {
	if (isVideoType(contentType)) return 'Video';
	return 'Doküman';
}

function getContentUrl(doc: SupportDocumentResponse): string {
	return doc.deliveryInfoList?.[0]?.link ?? doc.contentUrl ?? '';
}

function setCloudfrontCookies(delivery: ContentDeliveryInfoResponse): void {
	if (!delivery.link || !delivery.keyPairId || !delivery.policy || !delivery.signature) return;
	try {
		const { hostname } = new URL(delivery.link);
		const parts = hostname.split('.');
		const domain = `.${parts.slice(-2).join('.')}`;
		const attrs = `Domain=${domain}; Path=/; Secure; SameSite=None`;
		document.cookie = `CloudFront-Key-Pair-Id=${delivery.keyPairId}; ${attrs}`;
		document.cookie = `CloudFront-Policy=${delivery.policy}; ${attrs}`;
		document.cookie = `CloudFront-Signature=${delivery.signature}; ${attrs}`;
	} catch {
		// ignore URL parse errors
	}
}

const SupportDocumentsScreen: React.FC = () => {
	const { SupportDocumentControllerApi } = useApi();

	const [activeTab, setActiveTab] = useState<SupportDocTab>('my-docs');
	const [searchInput, setSearchInput] = useState('');
	const [myDocsPage, setMyDocsPage] = useState(1);
	const [guidesPage, setGuidesPage] = useState(1);
	const [selectedDoc, setSelectedDoc] = useState<SupportDocumentResponse | null>(null);

	const debouncedSearch = useDebounce(searchInput, 400);

	const { data: allDocs, isLoading } = useQueryProvider(
		[QUERY_KEYS.findSupportDocuments, debouncedSearch],
		() => SupportDocumentControllerApi.findSupportDocuments(undefined, debouncedSearch || undefined, 0, 200),
		{
			select: ({ data }) => data?.data?.content ?? [],
			refetchOnWindowFocus: false,
			keepPreviousData: true
		}
	);

	const myDocs = useMemo(() => (allDocs ?? []).filter(d => !isVideoType(d.contentType)), [allDocs]);
	const guides = useMemo(() => (allDocs ?? []).filter(d => isVideoType(d.contentType)), [allDocs]);

	const activeList = activeTab === 'my-docs' ? myDocs : guides;
	const activePage = activeTab === 'my-docs' ? myDocsPage : guidesPage;
	const setActivePage = activeTab === 'my-docs' ? setMyDocsPage : setGuidesPage;

	const totalPages = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));
	const pageItems = activeList.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE);

	const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInput(e.target.value);
		setMyDocsPage(1);
		setGuidesPage(1);
	}, []);

	const handleTabChange = useCallback((tab: SupportDocTab) => {
		setActiveTab(tab);
	}, []);

	const handleCardClick = useCallback((doc: SupportDocumentResponse) => {
		const delivery = doc.deliveryInfoList?.[0];
		if (delivery) setCloudfrontCookies(delivery);
		setSelectedDoc(doc);
	}, []);

	const handleCloseModal = useCallback(() => {
		setSelectedDoc(null);
	}, []);

	const handlePageChange = useCallback(
		(page: number) => {
			setActivePage(page);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},
		[setActivePage]
	);

	const contentUrl = selectedDoc ? getContentUrl(selectedDoc) : '';
	const isVideo = isVideoType(selectedDoc?.contentType);

	return (
		<div className="container">
			<div className="support-documents">
				<h1 className="support-documents__title">Destek Dokümanları</h1>

				<div className="support-documents__toolbar">
					<div className="support-documents__search">
						<i className="icons-magnifer" />
						<input
							type="text"
							placeholder="Doküman ara..."
							value={searchInput}
							onChange={handleSearchChange}
						/>
					</div>
				</div>

				<div className="support-documents__tabs">
					<button
						className={`support-documents__tab ${activeTab === 'my-docs' ? 'support-documents__tab--active' : ''}`}
						onClick={() => handleTabChange('my-docs')}>
						Eğitim Dökümanlarım
					</button>
					<button
						className={`support-documents__tab ${activeTab === 'guides' ? 'support-documents__tab--active' : ''}`}
						onClick={() => handleTabChange('guides')}>
						Eğitimler - Kılavuzlar
					</button>
				</div>

				<div className="support-documents__grid">
					{isLoading && (
						<div className="support-documents__loading">
							<LoadingSpinner loadingText="Yükleniyor..." />
						</div>
					)}

					{!isLoading && pageItems.length === 0 && (
						<div className="support-documents__empty">
							<i className="icons-document" />
							<p>Gösterilecek doküman bulunamadı.</p>
						</div>
					)}

					{!isLoading &&
						pageItems.map(doc => (
							<div
								key={doc.id}
								className="support-documents__card"
								onClick={() => handleCardClick(doc)}>
								<div className={`support-documents__card-thumb ${getCardThumbClass(doc.contentType)}`}>
									<i className={getCardIcon(doc.contentType)} />
									<span className={`support-documents__card-type-badge ${getBadgeClass(doc.contentType)}`}>{getBadgeLabel(doc.contentType)}</span>
								</div>
								<div className="support-documents__card-body">
									<span className="support-documents__card-title">{doc.contentName}</span>
									{doc.contentSubtypeName && <span className="support-documents__card-sub">{doc.contentSubtypeName}</span>}
								</div>
							</div>
						))}
				</div>

				{!isLoading && activeList.length > PAGE_SIZE && (
					<div className="support-documents__pagination">
						<Pagination
							currentPage={activePage}
							totalPages={totalPages}
							onPageChange={handlePageChange}
						/>
					</div>
				)}
			</div>

			<Modal
				isOpen={!!selectedDoc}
				onClose={handleCloseModal}
				hasHeader={false}
				size="lg"
				showCloseButton={false}
				closeOnClickOutside={true}
				closeOnEsc={true}>
				{selectedDoc && (
					<div className="support-doc-modal">
						<div className="support-doc-modal__header">
							<span className="support-doc-modal__title">{selectedDoc.contentName}</span>
							<button
								className="support-doc-modal__close"
								onClick={handleCloseModal}>
								<i className="icons-close" />
							</button>
						</div>
						<div className="support-doc-modal__body">
							{isVideo && contentUrl && (
								<div className="support-doc-modal__player-wrap">
									<ReactPlayer
										url={contentUrl}
										width="100%"
										height="100%"
										controls
										config={{
											file: {
												attributes: {
													controlsList: 'nodownload',
													disablePictureInPicture: true,
													onContextMenu: (e: React.MouseEvent) => e.preventDefault()
												}
											}
										}}
									/>
								</div>
							)}

							{!isVideo && contentUrl && (
								<iframe
									src={contentUrl}
									title={selectedDoc.contentName}
									className="support-doc-modal__iframe"
									allowFullScreen
								/>
							)}

							{!contentUrl && (
								<div className="support-doc-modal__unsupported">
									<i className="icons-document" />
									<p>İçerik şu anda görüntülenemiyor.</p>
								</div>
							)}
						</div>
					</div>
				)}
			</Modal>
		</div>
	);
};

export default SupportDocumentsScreen;
